import { useMemo } from 'react';
import usePageParams from './usePageParams';
import useResetPage from 'router/subrouters/Dash/subrouters/hooks/useResetPage';
import { transformValue } from 'components/ResolvePagination/utils';
import { getArrayLength } from 'utils';
import { PAGE_PARAM_KEYWORD } from 'data/global';

export const DEFAULT_LOCAL_LIMIT = 10;

type Config = {
  limit?: number;
  pageParam?: string;
  // Lets a shared component paginate for one caller and not for another: the
  // hook still runs, but hands back the untouched list.
  isEnabled?: boolean;
};

// Pages an in-memory array, with the current page read from (and reset in) the
// URL like every other paginated list in the app. For lists the BE hands over
// whole — server-paginated data uses `totalPages` from the response instead.
export default function useLocalPagination<T>(items: T[], config?: Config) {
  const {
    limit = DEFAULT_LOCAL_LIMIT,
    pageParam = PAGE_PARAM_KEYWORD,
    isEnabled = true,
  } = config || {};

  const page = usePageParams(pageParam);

  const totalPages = useMemo(
    () => (isEnabled ? Math.ceil(getArrayLength(items) / limit) : 0),
    [isEnabled, items, limit],
  );

  // Corrects the URL when the current page no longer exists, e.g. a filter
  // shrank the list while the user sat on page 5.
  useResetPage(totalPages, page, pageParam);

  const pageItems = useMemo(() => {
    if (!isEnabled || !totalPages || totalPages === 1) return items;

    // Clamped so an out-of-range page renders the first one rather than an
    // empty table for the render before useResetPage fixes the param.
    const currentPage = transformValue(+page || 1, totalPages);
    const start = (currentPage - 1) * limit;

    return items.slice(start, start + limit);
  }, [isEnabled, items, limit, page, totalPages]);

  // `limit` comes back out so a caller that leaves it to the default still
  // shows a "showing x to y" count that matches the pages it renders.
  return { page, limit, totalPages, pageItems };
}
